import {
  Component,
  createRef,
  KeyboardEvent,
  RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
// this is now included in bundle.config.js
import { autorun, toJS } from "mobx";
import { observer } from "mobx-react";
import { StoreContext } from "../../../stores/StoreContext";
import Tagify from "@yaireo/tagify";
import { AppContextType } from "../../../stores/StoreLoader";
import i18n from "../../../internationalization/i18n";

export interface ITag {
  value: string;
  __removed?: boolean;
}

export interface ITagElement {
  __tagifyTagData: ITag;
}

export interface ITagify {
  addTags: (tags: (ITag | undefined | string)[], cleanInput?: boolean) => void;
  replaceTag: (oldTag: ITagElement, newTag: ITag | undefined) => void;
  removeTags: (tag: string | ITagElement) => void;
  getTagElms: () => ITagElement[];
  createTagElem: (tag: ITag) => ITagElement;
  insertAfterTag: (tag: ITagElement, toInsert: ITagElement) => {};
  settings: {
    whitelist: any[];
  };
  DOM?: {
    input: HTMLInputElement;
    scope: HTMLElement;
  };
  removeAllTags: () => void;
}

export type TagifyRefObject = { tagify: ITagify };

export function ReactTagify({
  ref,
  onKeydown,
  onReady,
  onAdd,
  onRemove,
  onInput,
  settings = {},
  placeholder = "",
  initialValue = [],
  ...restProps
}: {
  ref: RefObject<any>;
  onKeydown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onReady?: () => void;
  onAdd?: (e: Event) => void;
  onInput?: (e: Event) => void;
  onRemove?: (e: Event) => void;
  settings?: object;
  placeholder?: string;
  initialValue?: string[] | Array<ITag>;
  [k: string]: any;
}) {
  const inputRef = useRef(null);
  const [tagify, setTagify] = useState<ITagify | null>(null);

  useImperativeHandle(ref, () => ({
    tagify: tagify,
  }));

  useEffect(() => {
    const t = new Tagify(inputRef.current, settings);
    t.on("keydown", onKeydown);
    t.on("add", onAdd);
    t.on("remove", onRemove);
    t.on("input", onInput);
    setTagify(t);
  }, []);

  useEffect(() => {
    if (tagify && initialValue) {
      tagify.addTags(initialValue);
    }
    if (onReady) {
      onReady();
    }
  }, [tagify]);

  useEffect(() => {
    if (tagify) {
      ["data", "aria"].forEach((p) =>
        tagify.DOM?.input.setAttribute(`${p}-placeholder`, placeholder),
      );
    }
  }, [placeholder]);

  return (
    <input
      ref={inputRef}
      placeholder={placeholder}
      className="notranslate"
      {...restProps}
    />
  );
}

type ITagsProps = {
  textCategorySearch: any;
  settings: any;
  onTabClosed: any;
  name: any;
  className: any;
  placeholder: any;
  autofocus: any;
  mode: any;
  initialValue: any;
};

@observer
class Tags extends Component<ITagsProps> {
  static contextType = StoreContext;
  private component;
  private tagify;
  private disabler;

  constructor(props) {
    super(props);

    this.state = {};
    this.component = createRef();
  }

  componentDidMount() {
    const { gridStore } = this.context as AppContextType;

    this.tagify = new Tagify(this.component.current, this.props.settings || {});
    const textCategorySearch = this.props.textCategorySearch;

    // Update Tagify's internal placeholder attributes
    if (this.tagify && this.props.placeholder) {
      ["data", "aria"].forEach((p) =>
        this.tagify.DOM?.input.setAttribute(
          `${p}-placeholder`,
          this.props.placeholder,
        ),
      );
    }

    this.disabler = autorun(() => {
      if (gridStore.searchCategories?.length > 0) {
        this.tagify.settings.whitelist = toJS(gridStore.searchCategories).map(
          (c) => {
            return {
              alias: c.alias,
              id: c.path.split(".")[c.path.split(".").length - 1],
              value: i18n.t(c.category_name),
            };
          },
        );
        this.tagify.dropdown.show.call(this.tagify);
      }

      // textCategorySearch refers to being able to input a category as text and filter by any matching categories
      if (!textCategorySearch) {
        // we shouldn't have to use this when searching for categories because all tagging should be handled by tagify
        this.tagify.removeAllTags();

        if (!gridStore.categoryFilters.allTopFiltersActive) {
          const values = Array.from(gridStore.categoryFilters.filters.values())
            // @ts-ignore
            .filter((category) => category.active)
            .map((category) => {
              return {
                // @ts-ignore
                id: category.id, // this is necessary to distinguish categories from text tags
                // @ts-ignore
                value: i18n.t(category.category_name),
              };
            });
          this.tagify.addTags(values, true);
        }
      }
    });
  }

  componentWillUnmount() {
    this.disabler();
  }

  onTabClosed = ({ detail: component }) => {
    this.props.onTabClosed &&
      this.props.onTabClosed(component.getAttribute("tabId"));
  };

  render() {
    const attrs = {
      ref: this.component,
      name: this.props.name,
      className: `${this.props.className} notranslate`,
      placeholder: this.props.placeholder,
      autoFocus: this.props.autofocus,
    };

    if (this.props.mode === "textarea") {
      return <textarea {...attrs} defaultValue={this.props.initialValue} />;
    } else {
      return <input {...attrs} defaultValue={this.props.initialValue} />;
    }
  }
}

export default Tags;
